import {
	CallFieldMappingConfigDescriptor,
	CallFieldMappingConfig,
	CallLogData,
	CreateJobCustomField,
	CreateJobsState,
	CreateJobState,
	CreateJobStep,
	MapCallFieldsStep
} from '../state/createJobState';
import { AppAction } from '../actions/appAction';
import produce from 'immer';
import {
	getCallLogDataResult,
	getColumnIndexesForMappingConfig,
	isCreateJobStepValid
} from '../selectors/createJobSelectors';
import { CustomFieldKindId } from '../../models/dto/customFieldKindId';
import { getLocalTimezoneOrDefault } from '../../helpers/dates';
import { CustomFieldValueSummary } from '../../models/dto/customField';
import { getUid } from '../../helpers/ids';
import { assertIsNever, conditionalProduce } from '../../helpers/utilities';
import cloneDeep from 'lodash/cloneDeep'
import { MappingRequirementType } from '../../models/mappingRequirementType';

const unloaded: CreateJobsState = { byEngagement: new Map() };
const kindsInDescendingOrder: CustomFieldKindId[] = [CustomFieldKindId.Engagement, CustomFieldKindId.Job, CustomFieldKindId.Call];
const requirementTypeSortOrder: Map<MappingRequirementType, number> = new Map([[MappingRequirementType.Required, 1], [MappingRequirementType.Recommended, 2], [MappingRequirementType.Optional, 3]]);

export function createJobReducer(state: CreateJobsState | undefined = unloaded, action: AppAction): CreateJobsState {
	switch (action.type) {
		case 'create-job/initialize':
			return replaceState(state, action.engagementId, getPristineState(action.customFields));
		case 'create-job/clear':
			return replaceState(state, action.engagementId, null);
		case 'create-job/previous':
			return updateState(state, action.engagementId, draft => {
				if (draft.prevSteps.length) {
					draft.nextSteps.unshift(draft.currentStep);
					draft.currentStep = draft.prevSteps.pop()!;
				}
			});
		case 'create-job/next':
			return updateStateConditionally(state, action.engagementId, (draft, original) => {
				let currentStep = draft.currentStep;
				
				if (!draft.nextSteps.length || !isCreateJobStepValid(currentStep, draft.callLogData))
					return false;
				
				if (currentStep.type === 'logFiles') {
					const result = getCallLogDataResult(currentStep);
					if (result.type === 'invalid') {
						// this should never happen as it should get
						// caught in isCreateJobStepValid()
						return false;
					}
					
					draft.callLogData = result.data;
					
					// reset the mappings for the case where the user
					// configured some mappings and then came back
					// and changed the the selected files.
					// also, need to use the original here otherwise
					// copying doesn't work
					draft.nextSteps = original.pristineMappingSteps.map(copy);
				}

				draft.prevSteps.push(currentStep);
				
				const nextStep = draft.nextSteps.shift()!;
				draft.currentStep = nextStep;
				
				return true;
			});
		case 'create-job/update-name':
			return updateState(state, action.engagementId, draft => {
				if (draft.currentStep.type === 'info') {
					draft.currentStep.name = action.name;
				}
			});
		case 'create-job/update-custom-field':
			return updateState(state, action.engagementId, draft => {
				if (draft.currentStep.type === 'info') {
					const field = draft.currentStep.customFields.find(f => f.fieldId === action.fieldId);
					if (field) {
						field.value = action.value;
					}
				}
			});
		case 'create-job/call-log-file-imported':
			return updateState(state, action.engagementId, draft => {
				if (draft.currentStep.type !== 'logFiles')
					return;
				
				draft.currentStep.files.unshift(action.file);
			});
		case 'create-job/remove-call-log-file':
			return updateState(state, action.engagementId, draft => {
				if (draft.currentStep.type !== 'logFiles')
					return;

				draft.currentStep.files = draft.currentStep.files.filter(f => f.id !== action.id);
			});
		case 'create-job/choose-call-log-xls-file-sheet':
			return updateState(state, action.engagementId, draft => {
				if (draft.currentStep.type !== 'logFiles')
					return;
				
				const file = draft.currentStep.files.find(f => f.id === action.fileId);
				if (file && file.type === 'xls') {
					const sheet = file.allSheets.find(s => s.sheetId === action.sheetId);
					if (sheet) {
						file.selectedSheet = sheet;
					}
				}
			});
		case 'create-job/update-call-field-mapping-config':
			return updateState(state, action.engagementId, draft => {
				if (draft.currentStep.type === 'mapCallFields') {
					const configIdx = draft.currentStep.mappings.findIndex(m => m.id === action.config.id);
					if (configIdx === -1) return;
					
					const config = draft.currentStep.mappings[configIdx];
					if (action.config.type !== config.type) return;
					
					// ensure that there is a 1-1 relationship between
					// mappings and columns by removing the selected column
					// from any other mappings first (this is a restriction we have
					// in place so the "table view" works - since that UI
					// only supports a 1-1 relationship)
					const indexes = getColumnIndexesForMappingConfig(action.config);
					draft.currentStep.mappings.forEach(m => {
						indexes.forEach(i => {
							removeColumnIndexFromMappingConfig(m, i);
						});
					});

					draft.currentStep.mappings.splice(configIdx, 1, action.config);
				}
			});
		case 'create-job/clear-call-field-mapping':
			return updateState(state, action.engagementId, draft => {
				if (draft.currentStep.type === 'mapCallFields') {
					draft.currentStep.mappings = draft.currentStep.mappings.map(m => {
						return m.id === action.mappingId ? getPristineCallFieldMappingConfig(m) : m;
					});
				}
			});
		case 'create-job/clear-call-field-mapping-by-column-index':
			return updateState(state, action.engagementId, draft => {
				if (draft.currentStep.type === 'mapCallFields') {
					draft.currentStep.mappings.forEach(m => {
						removeColumnIndexFromMappingConfig(m, action.columnIndex);
					});
				}
			});
		case 'create-job/update-request-status':
			if (action.status === 'complete') {
				return replaceState(state, action.engagementId, null);
			} else {
				return updateState(state, action.engagementId, draft => {
					draft.createRequestStatus = action.status;
				});				
			}
		default:
			return state;
	}
}

function removeColumnIndexFromMappingConfig(config: CallFieldMappingConfig, columnIndex: number): void {
	switch (config.type) {
		case 'direction':
			if (config.modeConfig.type === 'call' && config.modeConfig.columnIndex === columnIndex) {
				config.modeConfig.columnIndex = null;
			}
			break;
		case 'sessionId':
		case 'filename':
		case 'date':
		case 'basic':
			if (config.columnIndex === columnIndex) {
				config.columnIndex = null;
			}
			break;
		case 'participantName':
			if (config.modeConfig.type === 'singleField') {
				if (config.modeConfig.columnIndex === columnIndex) {
					config.modeConfig = { type: 'multiField', firstNameColumnIndex: null, lastNameColumnIndex: null };
				}
			} else {
				if (config.modeConfig.firstNameColumnIndex === columnIndex) {
					config.modeConfig.firstNameColumnIndex = null;
				}
				
				if (config.modeConfig.lastNameColumnIndex === columnIndex) {
					config.modeConfig.lastNameColumnIndex = null;
				}
			}
			break;
		default:
			// ensure all cases covered
			assertIsNever(config);
	}
}

function copy<T>(item: T): T {
	// we need to copy things because immer doesn't support
	// having the same object 2 places in the tree
	return cloneDeep(item);
}

function getPristineState(customFields: CustomFieldValueSummary[]): CreateJobState {
	const firstSteps: CreateJobStep[] = [
		{ 
			type: 'info',
			id: getUid(),
			name: '', 
			customFields: customFields.filter(f => (f.customFieldKindIds ?? []).includes(CustomFieldKindId.Job)).sort(getCustomFieldSorter(CustomFieldKindId.Job)).map<CreateJobCustomField>(f => {
				return {
					fieldId: f.id,
					required: isCustomFieldRequiredForLevel(f, CustomFieldKindId.Job),
					fieldDisplayName: f.displayName,
					value: f.value ?? '',
					defaultValue: f.value
				}
			})
		},
		{ 
			type: 'logFiles',
			id: getUid(),
			files: []
		}
	];
	
	const mappings: CallFieldMappingConfig[] = [
		// required
		getPristineCallFieldMappingConfig({ type: 'date', id: getUid(), destination: { type: 'field', field: 'startTime' }, displayName: 'Call start date/time', requirementType: MappingRequirementType.Required}),
		getPristineCallFieldMappingConfig({ type: 'filename', id: getUid(), destination: { type: 'field', field: 'fileName' }, maxLengthConfig: { maxLength: 500 }, displayName: 'Audio file', requirementType: MappingRequirementType.Required}),

		// not required
		getPristineCallFieldMappingConfig({type: 'sessionId', id: getUid(), destination: { type: 'field', field: 'sessionId'}, maxLengthConfig: { maxLength: 500 }, displayName: 'Call ID', requirementType: MappingRequirementType.Recommended, helpText: 'Call ID is a unique identifier assigned to each call'}),
		getPristineCallFieldMappingConfig({type: 'basic', id: getUid(), destination: { type: 'field', field: 'masterCallId'}, maxLengthConfig: { maxLength: 500 }, displayName: 'Master ID', requirementType: MappingRequirementType.Optional, helpText: 'Master ID is only needed when your calls are recorded in multiple segments and the master ID is needed to group the segments'}),
		getPristineCallFieldMappingConfig({type: 'direction', id: getUid(), destination: { type: 'field', field: 'direction'}, displayName: 'Call direction', requirementType: MappingRequirementType.Recommended}),
		getPristineCallFieldMappingConfig({type: 'basic', id: getUid(), destination: { type: 'field', field: 'numberDialed'}, maxLengthConfig: { maxLength: 20 }, displayName: 'Number dialed', requirementType: MappingRequirementType.Optional}),
		getPristineCallFieldMappingConfig({type: 'date', id: getUid(), destination: { type: 'field', field: 'endTime'}, displayName: 'Call end date/time', requirementType: MappingRequirementType.Optional}),
		getPristineCallFieldMappingConfig({type: 'participantName', id: getUid(), participantType: 'agent', firstMaxLengthConfig: { maxLength: 50 }, lastMaxLengthConfig: { maxLength: 50 }, displayName: 'Agent name', requirementType: MappingRequirementType.Recommended}),
		getPristineCallFieldMappingConfig({type: 'basic', id: getUid(), destination: { type: 'field', field: 'agentReferenceId'}, maxLengthConfig: { maxLength: 50 }, displayName: 'Agent ID', requirementType: MappingRequirementType.Recommended}),
		getPristineCallFieldMappingConfig({type: 'basic', id: getUid(), destination: { type: 'field', field: 'agentEmail'}, maxLengthConfig: { maxLength: 50 }, displayName: 'Agent email', requirementType: MappingRequirementType.Optional}),
		getPristineCallFieldMappingConfig({type: 'basic', id: getUid(), destination: { type: 'field', field: 'agentPhone'}, maxLengthConfig: { maxLength: 20 }, displayName: 'Agent phone', requirementType: MappingRequirementType.Optional}),
		getPristineCallFieldMappingConfig({type: 'participantName', id: getUid(), participantType: 'customer', firstMaxLengthConfig: { maxLength: 50 }, lastMaxLengthConfig: { maxLength: 50 }, displayName: 'Customer name', requirementType: MappingRequirementType.Recommended}),
		getPristineCallFieldMappingConfig({type: 'basic', id: getUid(), destination: { type: 'field', field: 'customerReferenceId'}, maxLengthConfig: { maxLength: 50 }, displayName: 'Customer ID', requirementType: MappingRequirementType.Optional}),
		getPristineCallFieldMappingConfig({type: 'basic', id: getUid(), destination: { type: 'field', field: 'customerEmail'}, maxLengthConfig: { maxLength: 50 }, displayName: 'Customer email', requirementType: MappingRequirementType.Optional}),
		getPristineCallFieldMappingConfig({type: 'basic', id: getUid(), destination: { type: 'field', field: 'customerPhone'}, maxLengthConfig: { maxLength: 20 }, displayName: 'Customer phone', requirementType: MappingRequirementType.Optional}),
		
		// custom fields
		...customFields.filter(f => (f.customFieldKindIds ?? []).includes(CustomFieldKindId.Call)).map<CallFieldMappingConfig>(f => getPristineCallFieldMappingConfig({ 
			type: 'basic', 
			id: getUid(), 
			destination: { type: 'customField', fieldId: f.id }, 
			maxLengthConfig: { maxLength: 500 }, 
			displayName: f.displayName,
			requirementType: isCustomFieldRequiredForLevel(f, CustomFieldKindId.Call) ? MappingRequirementType.Required : MappingRequirementType.Optional 
		}))
	]
	
	const mappingStep: MapCallFieldsStep = {
		type: 'mapCallFields',
		id: getUid(),
		mappings: ([...mappings]).sort((a, b) => {
			if (a.requirementType !== b.requirementType) {
				return (requirementTypeSortOrder.get(a.requirementType) ?? 0) - (requirementTypeSortOrder.get(b.requirementType) ?? 0);
			} else {
				return (a.displayName ?? '').localeCompare(b.displayName ?? '');
			}
		})
	};
	
	const allSteps = [
		...firstSteps,
		mappingStep
	];
	
	return {
		callLogData: getEmptyCallLogData(),
		prevSteps: [],
		currentStep: allSteps[0],
		nextSteps: allSteps.slice(1),
		pristineMappingSteps: [copy(mappingStep)],
		createRequestStatus: 'none'
	}
}

function getEmptyCallLogData(): CallLogData {
	return { headers: [], callRows: [] };
}

function getPristineCallFieldMappingConfig(descriptor: CallFieldMappingConfigDescriptor): CallFieldMappingConfig {
	switch (descriptor.type) {
		case 'date':
			return { ...descriptor, timezone: getLocalTimezoneOrDefault(), columnIndex: null };
		case 'filename':
			return { ...descriptor, columnIndex: null };
		case 'sessionId':
			return { ...descriptor, columnIndex: null };
		case 'direction':
			return { ...descriptor, modeConfig: { type: 'call', mappings: new Map(), columnIndex: null }};
		case 'participantName':
			return { ...descriptor, modeConfig: { type: 'multiField', firstNameColumnIndex: null, lastNameColumnIndex: null }};
		case 'basic':
			return { ...descriptor, columnIndex: null};
	}
}

function getCustomFieldSorter(level: CustomFieldKindId): (a: CustomFieldValueSummary, b: CustomFieldValueSummary) => number {
	return (a, b) => {
		const aRequired = isCustomFieldRequiredForLevel(a, level),
			bRequired = isCustomFieldRequiredForLevel(b, level);
		if (aRequired !== bRequired) {
			return aRequired ? -1 : 1;
		}

		return (a.displayName ?? '').localeCompare(b.displayName ?? '');
	}
}

function getTopLevelKindId(kinds: CustomFieldKindId[] | nundefined): CustomFieldKindId | nundefined {
	if (!kinds) return null;

	return kindsInDescendingOrder.find(k => kinds.includes(k));
}

function isCustomFieldRequiredForLevel(summary: CustomFieldValueSummary, level: CustomFieldKindId): boolean {
	return summary.required && level === getTopLevelKindId(summary.customFieldKindIds);
}

function updateState(state: CreateJobsState, engagementId: number, update: (draft: CreateJobState) => void): CreateJobsState {
	return produce(state, draft => {
		const createJobState = draft.byEngagement.get(engagementId);
		if (!createJobState)
			return;
		
		update(createJobState);
	});
}

function updateStateConditionally(state: CreateJobsState, engagementId: number, update: (draft: CreateJobState, original: CreateJobState) => boolean): CreateJobsState {
	return conditionalProduce(state, draft => {
		const createState = draft.byEngagement.get(engagementId);
		const origCreateState = state.byEngagement.get(engagementId);
		if (!createState || !origCreateState)
			return false;
		
		return update(createState, origCreateState);
	});
}

function replaceState(state: CreateJobsState, engagementId: number, replacement: CreateJobState | null): CreateJobsState {
	return produce(state, draft => {
		if (!replacement) {
			draft.byEngagement.delete(engagementId);
		} else {
			draft.byEngagement.set(engagementId, replacement);
		}
	})
}
